import {CollateralTableRow} from "./CollateralTable";
import {EquityCollateral} from "../../../../../types/PortfolioMarginResult";
import {CollateralMovement, CollateralMovementType} from "../PortfolioCollateral";
import {LodgedCollateral} from "./LodgedCollateral";
import {Optional} from "../../../../../js/util/Optional";

export class LodgedCollateralTableRow implements CollateralTableRow<LodgedCollateral> {

    private calculatedRow: LodgedCollateral;

    public constructor(private lodged?: EquityCollateral,
                       public pendingLodge?: CollateralMovement,
                       private pendingUnlodge?: CollateralMovement) {
        this.calculateActualRowValue(lodged, pendingLodge, pendingUnlodge);
    }

    private calculateActualRowValue(lodged?: EquityCollateral,
                                    pendingLodge?: CollateralMovement,
                                    pendingUnlodge?: CollateralMovement) {
        if (lodged != null && pendingLodge == null && pendingUnlodge == null) {
            this.calculatedRow = this.rowFromCollateral(lodged);
        } else if (lodged == null && pendingLodge != null && pendingUnlodge == null) {
            this.calculatedRow = this.rowFromPending(pendingLodge);
        } else if (lodged == null && pendingLodge == null && pendingUnlodge != null) {
            this.calculatedRow = this.rowFromPending(pendingUnlodge);
        } else if (lodged != null && (pendingLodge != null || pendingUnlodge != null)) {
            this.calculatedRow = this.merge(lodged, pendingLodge, pendingUnlodge);
        } else {
            throw Error("Unable to create a row from two null values");
        }
    }

    private rowFromCollateral(lodged: EquityCollateral): LodgedCollateral {
        const used = lodged.quantity - lodged.available;
        const utilisation = used / lodged.quantity;

        const totalValue = (lodged.quantity * lodged.price) * (1 - lodged.haircut);

        return {
            code: lodged.issuer,
            price: lodged.price,
            quantity: lodged.quantity,
            haircut: lodged.haircut,
            specificCover: lodged.specificCover,
            value: totalValue,
            used,
            utilisation
        };
    }

    private rowFromPending(pending: CollateralMovement): LodgedCollateral {
        const value = pending.price * pending.quantity;
        const haircutValue = value * (1 - pending.haircut);
        const isSpecificCover = pending.type === CollateralMovementType.SPECIFIC;
        return {
            code: pending.code,
            price: pending.price,
            quantity: pending.quantity,
            haircut: pending.haircut,
            specificCover: isSpecificCover,
            value: haircutValue,
            used: 0,
            utilisation: 0
        };
    }

    private merge(lodged: EquityCollateral,
                  pendingLodge?: CollateralMovement,
                  pendingUnlodge?: CollateralMovement): LodgedCollateral {
        const pendingLodgeQuanitity = Optional.of(pendingLodge)
                                              .map(pl => pl.quantity)
                                              .orElse(0);
        const pendingUnlodgeQuantity = Optional.of(pendingUnlodge)
                                               .map(pu => pu.quantity)
                                               .orElse(0);
        const totalQuantity = lodged.quantity + pendingLodgeQuanitity - pendingUnlodgeQuantity;
        const used = totalQuantity - lodged.available;
        const utilisation = used / totalQuantity;
        const totalValue = totalQuantity * lodged.price;

        return {
            code: lodged.issuer,
            price: lodged.price,
            quantity: lodged.quantity,
            haircut: lodged.haircut,
            specificCover: lodged.specificCover,
            value: totalValue,
            used,
            utilisation
        };
    }

    public get code(): string {
        return this.calculatedRow.code;
    }

    public get data(): LodgedCollateral {
        return this.calculatedRow;
    }

    public get pending(): CollateralMovement | undefined {
        return this.pendingLodge || this.pendingUnlodge;
    }

    public get hasPending(): boolean {
        return this.pending != null;
    }
}
